/**
 * Trim, speed, loop and sound for the selected video clip layer, plus the
 * media status badge ("Media missing — re-import", "This browser only").
 *
 * The clip descriptor lives on the Fabric object, so edits go through the
 * canvas's own history: sliders write live and commit one history entry at
 * gesture end, so one drag is one undo step.
 */

import { useEffect, useState } from 'react';
import type { fabric } from 'fabric';
import type { VideoClipRef } from '@canvas/shared-types';
import { replaceVideoMedia, type CraftaraVideoObject } from '@/lib/video/fabricVideoObject';
import { VIDEO_MEDIA_STATUS_EVENT } from '@/lib/video/liveVideoLayers';
import { formatTimecode } from '@/lib/video/sceneTimeline';
import { uploadProjectVideo } from '@/lib/video/videoAssetUpload';
import { clampClip, clipPlayMs, MIN_CLIP_MS } from '@/lib/video/videoClip';
import { VIDEO_CLIP_CHANGED_EVENT } from '@/lib/video/videoEvents';
import { useProjectStore } from '@/stores/projectStore';

const RATE_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];

interface VideoClipPropertiesProps {
  canvas: fabric.Canvas;
  obj: CraftaraVideoObject;
  onChange?: () => void;
}

function loadPoster(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not read the video’s first frame'));
    img.src = url;
  }).finally(() => URL.revokeObjectURL(url));
}

export default function VideoClipProperties({ canvas, obj, onChange }: VideoClipPropertiesProps) {
  const [clip, setClip] = useState<VideoClipRef>(obj.craftaraVideo);
  const [status, setStatus] = useState(obj._mediaStatus);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    setClip(obj.craftaraVideo);
    setStatus(obj._mediaStatus);
    setNote(null);
    const onStatus = (e: Event) => {
      if ((e as CustomEvent<{ obj?: unknown }>).detail?.obj === obj) setStatus(obj._mediaStatus);
    };
    window.addEventListener(VIDEO_MEDIA_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(VIDEO_MEDIA_STATUS_EVENT, onStatus);
  }, [obj]);

  const notifyLive = () =>
    window.dispatchEvent(new CustomEvent(VIDEO_CLIP_CHANGED_EVENT, { detail: { obj } }));

  /** Write without recording history (mid-gesture). */
  const write = (patch: Partial<VideoClipRef>) => {
    const next = clampClip({ ...obj.craftaraVideo, ...patch });
    obj.set('craftaraVideo', next);
    setClip(next);
    notifyLive();
  };
  const commit = () => onChange?.();
  /** Write and record one history entry (discrete controls). */
  const writeNow = (patch: Partial<VideoClipRef>) => {
    write(patch);
    commit();
  };

  const reimport = async (file: File) => {
    setBusy(true);
    setNote(null);
    try {
      const projectId = useProjectStore.getState().currentProject?.projectId ?? '';
      const imported = await uploadProjectVideo({ file, projectId });
      const prev = obj.craftaraVideo;
      // Keep the edit (trim, speed, sound) where the new file allows it.
      const next = clampClip({
        ...imported.clip,
        trimStartMs: prev.trimStartMs,
        trimEndMs: prev.trimEndMs,
        rate: prev.rate,
        loop: prev.loop,
        muted: prev.muted,
        volume: prev.volume,
      });
      replaceVideoMedia(obj, next, await loadPoster(imported.posterBlob), imported.posterUrl);
      setClip(next);
      setStatus(obj._mediaStatus);
      canvas.requestRenderAll();
      commit();
      notifyLive();
      setNote(imported.backedUp ? 'Media replaced' : 'Media replaced — stored in this browser only');
    } catch (err) {
      setNote(err instanceof Error ? err.message : 'Re-import failed');
    } finally {
      setBusy(false);
    }
  };

  const gesture = {
    onPointerUp: commit,
    onKeyUp: commit,
  };

  return (
    <div className="vt-clip-props" role="group" aria-label="Video clip">
      <h4 className="vt-clip-title">
        Video clip
        {status === 'missing' && <span className="vt-clip-badge vt-clip-badge-warn">Media missing — re-import</span>}
        {status !== 'missing' && clip.source === 'idb' && (
          <span className="vt-clip-badge" title="Sign in to back this clip up to the cloud">
            This browser only
          </span>
        )}
      </h4>

      <label className="vt-audio-field">
        <span>Start</span>
        <input
          type="range"
          min={0}
          max={Math.max(0, clip.durationMs - MIN_CLIP_MS)}
          step={50}
          value={clip.trimStartMs}
          {...gesture}
          onChange={(e) => {
            const trimStartMs = Number(e.target.value);
            write({ trimStartMs, trimEndMs: Math.max(clip.trimEndMs, trimStartMs + MIN_CLIP_MS) });
          }}
        />
        <output>{formatTimecode(clip.trimStartMs)}</output>
      </label>
      <label className="vt-audio-field">
        <span>End</span>
        <input
          type="range"
          min={MIN_CLIP_MS}
          max={clip.durationMs}
          step={50}
          value={clip.trimEndMs}
          {...gesture}
          onChange={(e) => {
            const trimEndMs = Number(e.target.value);
            write({ trimEndMs, trimStartMs: Math.min(clip.trimStartMs, trimEndMs - MIN_CLIP_MS) });
          }}
        />
        <output>{formatTimecode(clip.trimEndMs)}</output>
      </label>

      <div className="vt-clip-row">
        <label className="vt-clip-inline">
          Speed
          <select value={clip.rate} onChange={(e) => writeNow({ rate: Number(e.target.value) })}>
            {RATE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}×
              </option>
            ))}
          </select>
        </label>
        <label className="vt-clip-inline">
          <input type="checkbox" checked={clip.loop} onChange={(e) => writeNow({ loop: e.target.checked })} />
          Loop
        </label>
        <label className="vt-clip-inline">
          <input type="checkbox" checked={clip.muted} onChange={(e) => writeNow({ muted: e.target.checked })} />
          Mute
        </label>
      </div>

      {!clip.muted && (
        <label className="vt-audio-field">
          <span>Volume</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={clip.volume}
            {...gesture}
            onChange={(e) => write({ volume: Number(e.target.value) })}
          />
          <output>{Math.round(clip.volume * 100)}%</output>
        </label>
      )}

      <p className="field-hint">
        Plays from the start of its scene for {(clipPlayMs(clip) / 1000).toFixed(1)}s
        {clip.loop ? ', looping' : ', then holds its last frame'}. Sound is mixed into
        the export; preview stays quiet so it doesn’t drift against the music.
      </p>

      <label className={`vt-btn ${busy ? 'disabled' : ''}`}>
        {busy ? 'Importing…' : status === 'missing' ? 'Re-import media…' : 'Replace media…'}
        <input
          type="file"
          accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
          hidden
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void reimport(file);
          }}
        />
      </label>
      {note && <p className="field-hint">{note}</p>}
    </div>
  );
}
